import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  ArrowCounterClockwiseIcon,
  CopyIcon,
  DotsThreeVerticalIcon,
  FloppyDiskIcon,
  GiftIcon,
} from '@phosphor-icons/react';
import { useToaster } from '../../../shared/ui';
import type { Payment, Currency } from '../../../shared/utils';
import type { AppDispatch } from '../store/store';
import { setRetryPayment, saveTemplate } from '../store/paymentSlice';
import { PaymentStatusTag } from './PaymentStatusTag';
import { formatDate, formatMoney, maskIban } from '../utils/format';
import './PaymentRow.css';

interface PaymentRowProps {
  payment: Payment;
  showActions?: boolean;
}

export const PaymentRow = ({
  payment,
  showActions = false,
}: PaymentRowProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const toaster = useToaster();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{
    top: number;
    right: number;
  } | null>(null);

  const handleClaimRewards = () => {
    navigate({ to: '/rewards', search: { payment_id: payment.id } });
  };

  const handleViewRewards = () => {
    navigate({ to: '/rewards/history', search: { payment_id: payment.id } });
  };

  const handleUseAsTemplate = () => {
    dispatch(
      setRetryPayment({
        amount: String(payment.amount),
        currency: payment.currency as Currency,
        recipientName: payment.recipientName,
        // IBAN intentionally excluded — user must re-enter for security
        reference: payment.reference,
      })
    );
    navigate({ to: '/pay-rent' });
  };

  const handleSaveTemplate = () => {
    dispatch(
      saveTemplate({
        id: payment.id,
        name: `${formatMoney(payment.amount, payment.currency, lang)} · ${payment.recipientName} · ${payment.reference.slice(0, 30)}`,
        amount: String(payment.amount),
        currency: payment.currency as Currency,
        recipientName: payment.recipientName,
        reference: payment.reference,
      })
    );
    toaster.success(t('myPaymentsPage.templateSaved'));
  };

  const isRetryable =
    payment.status === 'CANCELLED' || payment.status === 'FAILED';

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(payment.id);
      toaster.success(t('myPaymentsPage.idCopied'));
    } catch {
      toaster.info(payment.id);
    }
  };

  const eligible = !!payment.rewardInfo?.isEligibleForRewards;
  const claimed = !!payment.rewardInfo?.hasClaimedRewards;

  const primaryAction = eligible
    ? {
        label: t('myPaymentsPage.selectRewards'),
        style: 'mc-btn--primary',
        onClick: handleClaimRewards,
      }
    : claimed
      ? {
          label: t('myPaymentsPage.viewRewards'),
          style: 'mc-btn--outline',
          onClick: handleViewRewards,
        }
      : isRetryable
        ? {
            label: t('myPaymentsPage.tryAgain'),
            style: 'mc-btn--primary',
            onClick: handleUseAsTemplate,
          }
        : null;

  const menuItems = [
    eligible &&
      claimed && {
        label: t('myPaymentsPage.viewRewards'),
        icon: <GiftIcon size={18} />,
        onClick: handleViewRewards,
      },
    !isRetryable && {
      label: t('myPaymentsPage.useTemplate'),
      icon: <ArrowCounterClockwiseIcon size={18} />,
      onClick: handleUseAsTemplate,
    },
    payment.status === 'EXECUTED' && {
      label: t('myPaymentsPage.saveTemplate'),
      icon: <FloppyDiskIcon size={18} />,
      onClick: handleSaveTemplate,
    },
  ].filter(Boolean) as Array<{
    label: string;
    icon: ReactNode;
    onClick: () => void;
  }>;

  // Fixed positioning so the menu isn't clipped by the scrollable table wrapper
  const toggleMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (menuPos) {
      setMenuPos(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 6,
      right: window.innerWidth - rect.right,
    });
  };

  useEffect(() => {
    if (!menuPos) return;
    const close = (event: Event) => {
      if (
        event.type === 'mousedown' &&
        menuRef.current?.contains(event.target as Node)
      ) {
        return;
      }
      setMenuPos(null);
    };
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [menuPos]);

  return (
    <tr>
      <td>
        {formatDate(payment.createdAt, lang)}
        {showActions && (
          <button
            type="button"
            className="payment-row__id"
            onClick={handleCopyId}
            title={`${t('myPaymentsPage.copyId')}: ${payment.id}`}
            aria-label={t('myPaymentsPage.copyId')}
          >
            ID {payment.id.slice(0, 8)}…
            <CopyIcon size={13} />
          </button>
        )}
      </td>
      <td>
        <strong>{payment.recipientName}</strong>
        <div className="mc-muted mc-small">
          {maskIban(payment.recipientIban)}
        </div>
      </td>
      <td>
        <strong>{formatMoney(payment.amount, payment.currency, lang)}</strong>
        {!!payment.pointsEarned && payment.pointsEarned > 0 && (
          <div className="mc-small payment-row__points">
            {t('myPaymentsPage.points', { points: payment.pointsEarned })}
          </div>
        )}
      </td>
      <td>{payment.reference}</td>
      <td>
        <PaymentStatusTag status={payment.status} />
      </td>
      {showActions && (
        <td>
          <div className="payment-row__actions">
            {primaryAction && (
              <button
                type="button"
                className={`mc-btn mc-btn--small ${primaryAction.style}`}
                onClick={primaryAction.onClick}
              >
                {primaryAction.label}
              </button>
            )}
            {menuItems.length > 0 && (
              <div className="payment-row__more" ref={menuRef}>
                <button
                  type="button"
                  className="payment-row__more-btn"
                  aria-label={t('myPaymentsPage.moreActions')}
                  title={t('myPaymentsPage.moreActions')}
                  aria-haspopup="menu"
                  aria-expanded={menuPos !== null}
                  onClick={toggleMenu}
                >
                  <DotsThreeVerticalIcon size={20} weight="bold" />
                </button>
                {menuPos && (
                  <div
                    className="payment-row__menu"
                    role="menu"
                    style={{ top: menuPos.top, right: menuPos.right }}
                  >
                    {menuItems.map(item => (
                      <button
                        key={item.label}
                        type="button"
                        role="menuitem"
                        className="payment-row__menu-item"
                        onClick={() => {
                          setMenuPos(null);
                          item.onClick();
                        }}
                      >
                        {item.icon}
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </td>
      )}
    </tr>
  );
};
